/**
 * Client-side encryption for login credentials stored in localStorage.
 * Uses AES-GCM with a key derived via PBKDF2 so credentials are not stored in plaintext.
 */

const CREDENTIALS_KEY_SALT = "offerz-login-credentials-salt-v1";
const CREDENTIALS_KEY_PASSWORD = "offerz-login-credentials-key-v1";

async function getCredentialsKey(): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(CREDENTIALS_KEY_PASSWORD),
    "PBKDF2",
    false,
    ["deriveBits", "deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode(CREDENTIALS_KEY_SALT),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * Encrypts login credentials for localStorage. Returns base64 string.
 */
export async function encryptCredentials(credentials: LoginCredentials): Promise<string> {
  const key = await getCredentialsKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(credentials));
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypts login credentials from localStorage.
 * Supports legacy plaintext JSON for backwards compatibility.
 */
export async function decryptCredentials(encryptedOrPlain: string): Promise<LoginCredentials> {
  if (!encryptedOrPlain || encryptedOrPlain.trim() === "") {
    throw new Error("No credentials data");
  }
  // Legacy: if it looks like JSON (starts with {), try parsing as plaintext
  const trimmed = encryptedOrPlain.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as LoginCredentials;
      if (typeof parsed?.email === "string" && typeof parsed?.password === "string") {
        return parsed;
      }
    } catch {
      // fall through to decrypt
    }
  }
  const key = await getCredentialsKey();
  const combined = Uint8Array.from(atob(trimmed), (c) => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const data = combined.slice(12);
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data);
  const parsed = JSON.parse(new TextDecoder().decode(decrypted)) as LoginCredentials;
  if (typeof parsed?.email !== "string" || typeof parsed?.password !== "string") {
    throw new Error("Invalid credentials format");
  }
  return parsed;
}
